import * as Cesium from "cesium";
import { CENTER } from "./effectConfig";
import {
  FOCUS_CORE,
  FOCUS_CONE,
  FOCUS_STATION,
  FOCUS_OLDTOWN,
  FOCUS_HUABO,
} from "@/utils/point";

/** 告警等级样式 */
export const ALERT_LEVELS = {
  critical: { name: "紧急", color: "#ff5252", colorHex: "#ff5252" },
  warning: { name: "重要", color: "#ffb300", colorHex: "#ffb300" },
  info: { name: "一般", color: "#40c4ff", colorHex: "#40c4ff" },
};

/**
 * 演示用假告警（环绕银川关键点）
 */
export const DEMO_ALERTS = [
  {
    id: "a1",
    title: "道路拥堵",
    level: "warning",
    type: "交通",
    place: "银川站东广场",
    time: "14:21",
    lng: FOCUS_STATION.lng + 0.002,
    lat: FOCUS_STATION.lat + 0.001,
  },
  {
    id: "a2",
    title: "管网压力异常",
    level: "critical",
    type: "生命线",
    place: "核心区供水节点",
    time: "14:18",
    lng: FOCUS_CORE.lng + 0.003,
    lat: FOCUS_CORE.lat - 0.002,
  },
  {
    id: "a3",
    title: "人流密度偏高",
    level: "info",
    type: "安防",
    place: "光锥示范点",
    time: "14:12",
    lng: FOCUS_CONE.lng,
    lat: FOCUS_CONE.lat,
  },
  {
    id: "a4",
    title: "井盖位移告警",
    level: "warning",
    type: "市政",
    place: "兴庆老城南街",
    time: "14:05",
    lng: FOCUS_OLDTOWN.lng - 0.004,
    lat: FOCUS_OLDTOWN.lat + 0.002,
  },
  {
    id: "a5",
    title: "水位监测提醒",
    level: "info",
    type: "水务",
    place: "花博园西侧水系",
    time: "13:58",
    lng: FOCUS_HUABO.lng - 0.006,
    lat: FOCUS_HUABO.lat - 0.003,
  },
  {
    id: "a6",
    title: "电力负荷告警",
    level: "critical",
    type: "能源",
    place: "金凤变电站周边",
    time: "13:50",
    lng: CENTER.lng - 0.02,
    lat: CENTER.lat + 0.01,
  },
];

export function summarizeAlerts(list = DEMO_ALERTS) {
  const summary = { total: list.length, critical: 0, warning: 0, info: 0 };
  for (const a of list) {
    if (summary[a.level] != null) summary[a.level] += 1;
  }
  return summary;
}

/**
 * 地图告警闪点 + HTML 清晰标签
 */
export default class AlertLayer {
  constructor(viewer, options = {}) {
    this.viewer = viewer;
    this.groundHeight = options.groundHeight ?? 1110;
    this.alerts = options.alerts ?? DEMO_ALERTS;
    this.dataSource = new Cesium.CustomDataSource("告警演示");
    viewer.dataSources.add(this.dataSource);
    this._entities = new Map();
    this._overlays = new Map();
    this._overlayRoot = null;
    this._tick = 0;
    this._removePulse = null;
    this._removePost = null;
    this._visible = true;
    this._build();
    this._buildOverlays();
    this._startPulse();
    this._startOverlaySync();
  }

  _build() {
    for (const a of this.alerts) {
      const level = ALERT_LEVELS[a.level] || ALERT_LEVELS.info;
      const color = Cesium.Color.fromCssColorString(level.color);
      const pos = Cesium.Cartesian3.fromDegrees(
        a.lng,
        a.lat,
        this.groundHeight + 40
      );
      const entity = this.dataSource.entities.add({
        id: `alert-${a.id}`,
        name: a.title,
        position: pos,
        point: {
          pixelSize: 14,
          color: color,
          outlineColor: Cesium.Color.WHITE,
          outlineWidth: 2,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          scaleByDistance: new Cesium.NearFarScalar(500, 1.2, 60000, 0.55),
        },
        ellipse: {
          semiMajorAxis: 180,
          semiMinorAxis: 180,
          material: color.withAlpha(0.22),
          height: this.groundHeight + 8,
          outline: true,
          outlineColor: color.withAlpha(0.65),
          outlineWidth: 2,
        },
        properties: a,
      });
      this._entities.set(a.id, { entity, color, baseAlpha: 0.22 });
    }
  }

  _buildOverlays() {
    const root = document.createElement("div");
    root.className = "alert-overlay-root";
    this.viewer.container.appendChild(root);
    this._overlayRoot = root;

    for (const a of this.alerts) {
      const el = document.createElement("div");
      el.className = `alert-map-label alert-map-label--${a.level}`;
      el.textContent = a.title;
      el.style.display = "none";
      root.appendChild(el);
      this._overlays.set(a.id, {
        el,
        lng: a.lng,
        lat: a.lat,
        cartesian: Cesium.Cartesian3.fromDegrees(
          a.lng,
          a.lat,
          this.groundHeight + 40
        ),
      });
    }
  }

  _startOverlaySync() {
    const scratch = new Cesium.Cartesian2();
    this._removePost = this.viewer.scene.postRender.addEventListener(() => {
      if (!this._visible || !this._overlayRoot) return;
      const scene = this.viewer.scene;
      const canvas = scene.canvas;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;

      for (const item of this._overlays.values()) {
        const win = Cesium.SceneTransforms.worldToWindowCoordinates(
          scene,
          item.cartesian,
          scratch
        );
        if (!win || win.x < -40 || win.y < -40 || win.x > w + 40 || win.y > h + 40) {
          item.el.style.display = "none";
          continue;
        }
        item.el.style.display = "block";
        item.el.style.transform = `translate(${win.x}px, ${win.y - 14}px) translate(-50%, -100%)`;
      }
    });
  }

  _startPulse() {
    this._removePulse = this.viewer.scene.preUpdate.addEventListener(() => {
      if (!this.dataSource.show) return;
      this._tick += 0.05;
      const wave = 0.5 + 0.5 * Math.sin(this._tick);
      for (const { entity, color, baseAlpha } of this._entities.values()) {
        if (entity.point) {
          entity.point.pixelSize = 10 + wave * 10;
          entity.point.color = color.withAlpha(0.55 + wave * 0.45);
        }
        if (entity.ellipse) {
          const r = 120 + wave * 160;
          entity.ellipse.semiMajorAxis = r;
          entity.ellipse.semiMinorAxis = r;
          entity.ellipse.material = color.withAlpha(
            baseAlpha * (0.45 + wave * 0.55)
          );
        }
      }
    });
  }

  setVisible(show) {
    this._visible = show;
    this.dataSource.show = show;
    if (this._overlayRoot) {
      this._overlayRoot.style.display = show ? "block" : "none";
    }
  }

  setGroundHeight(h) {
    this.groundHeight = h;
    for (const a of this.alerts) {
      const item = this._entities.get(a.id);
      if (!item) continue;
      item.entity.position = Cesium.Cartesian3.fromDegrees(
        a.lng,
        a.lat,
        h + 40
      );
      if (item.entity.ellipse) {
        item.entity.ellipse.height = h + 8;
      }
      const overlay = this._overlays.get(a.id);
      if (overlay) {
        overlay.cartesian = Cesium.Cartesian3.fromDegrees(a.lng, a.lat, h + 40);
      }
    }
  }

  flyTo(alertId, duration = 1.8) {
    const a = this.alerts.find((x) => x.id === alertId);
    if (!a || !this.viewer) return;
    const target = Cesium.Cartesian3.fromDegrees(
      a.lng,
      a.lat,
      this.groundHeight + 40
    );
    this.viewer.camera.flyToBoundingSphere(new Cesium.BoundingSphere(target, 60), {
      offset: new Cesium.HeadingPitchRange(
        0,
        Cesium.Math.toRadians(-42),
        1200
      ),
      duration,
    });
  }

  destroy() {
    if (this._removePulse) this._removePulse();
    if (this._removePost) this._removePost();
    this._overlayRoot?.remove();
    this._overlayRoot = null;
    this._overlays.clear();
    this.viewer.dataSources.remove(this.dataSource, true);
  }
}
